<template>
  <div class="p2p-mapping-container" :style="backgroundStyle">
    <!-- 顶部导航栏 -->
    <div class="top-nav">
      <div class="nav-container">
        <div class="client-tabs" :class="{ 'mobile-tabs': isMobile }">
          <div
            v-for="id in p2pClientCount"
            :key="id"
            class="client-tab"
            :class="{ 'active': currentId === id - 1 }"
            @click="switchClient(id - 1)"
          >
            {{ $t('p2pClientSettings.clientLabel') }} {{ id - 1 }}
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="row">
        <div class="col-md-12">
          <input type="hidden" name="id" id="id" :value="currentId" />

          <!-- P2P基础设置卡片 -->
          <div class="settings-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('p2pClientSettings.basicSettings') }}（{{ $t('p2pClientSettings.clientLabel') }}{{ currentId }}）</h3>
              <div class="btn-group">
                <el-button type="info" size="small" @click="showLogDialog">
                  <el-icon><Document /></el-icon>
                  {{ $t('p2pClientSettings.viewLogs') }}
                </el-button>
                <el-button
                  v-if="p2pClient.status"
                  type="primary"
                  size="small"
                  @click="stopService"
                >
                  {{ $t('p2pClientSettings.stop') }}
                </el-button>
                <el-button
                  v-else
                  type="primary"
                  size="small"
                  @click="showEditClientDialog"
                >
                  {{ $t('p2pClientSettings.editConfig') }}
                </el-button>
              </div>
            </div>
            <div class="card-body">
              <el-form label-position="top">
                <div class="form-row">
                  <div class="form-item" :data-label="$t('p2pClientSettings.name')">
                    <div class="item-label">{{ $t('p2pClientSettings.name') }}</div>
                    <div class="item-value">
                      <el-input
                        :value="p2pClientForm.name"
                        readonly
                      />
                    </div>
                  </div>

                  <div class="form-item" :data-label="$t('p2pClientSettings.serverAddress')">
                    <div class="item-label">{{ $t('p2pClientSettings.serverAddress') }}</div>
                    <div class="item-value">
                      <span>{{ p2pServerAddr }}</span>
                    </div>
                  </div>

                  <div class="form-item" :data-label="$t('p2pClientSettings.secretKey')">
                    <div class="item-label">{{ $t('p2pClientSettings.secretKey') }}</div>
                    <div class="item-value">
                      <el-input
                        :value="p2pClientForm.token"
                        readonly
                      />
                    </div>
                  </div>

                  <div class="form-item" :data-label="$t('p2pClientSettings.secondKey')">
                    <div class="item-label">{{ $t('p2pClientSettings.secondKey') }}</div>
                    <div class="item-value">
                      <el-input
                        :value="p2pClientForm.secondToken"
                        readonly
                      />
                    </div>
                  </div>

                  <div class="form-item" :data-label="$t('p2pClientSettings.enableRelay')">
                    <div class="item-label">{{ $t('p2pClientSettings.enableRelay') }}</div>
                    <div class="item-value">
                      <el-input
                        :value="p2pClientForm.brFlag === 'on' ? $t('p2pClientSettings.enabled') : $t('p2pClientSettings.disabled')"
                        readonly
                      />
                    </div>
                  </div>

                  <div class="form-item" :data-label="$t('p2pClientSettings.exportPort')">
                    <div class="item-label">{{ $t('p2pClientSettings.exportPort') }}</div>
                    <div class="item-value">
                      <span>{{ exportPort }}</span>
                    </div>
                  </div>

                  <div class="form-item" :data-label="$t('p2pClientSettings.status')">
                    <div class="item-label">{{ $t('p2pClientSettings.status') }}</div>
                    <div class="item-value">
                      <div :class="getStatusClass(p2pClient.result)">
                        {{ p2pClient.result }}
                      </div>
                    </div>
                  </div>
                </div>
              </el-form>
            </div>
          </div>

          <!-- P2P节点信息卡片 -->
          <div class="settings-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('p2pClientSettings.nodeInfo') }}{{ peerFlag ? `(${localIP})` : '' }}</h3>
            </div>
            <div class="card-body">
              <div class="node-info">
                <div class="info-row" :data-label="$t('p2pClientSettings.localNatType')">
                  <div class="info-label">{{ $t('p2pClientSettings.localNatType') }}</div>
                  <div class="info-value">{{ localNatType }}</div>
                </div>
                <div class="info-row" :data-label="$t('p2pClientSettings.localConnectAddr')">
                  <div class="info-label">{{ $t('p2pClientSettings.localConnectAddr') }}</div>
                  <div class="info-value">{{ localConnectAddr }}</div>
                </div>

                <template v-if="peerFlag">
                  <div class="info-row" :data-label="$t('p2pClientSettings.peerNatType')">
                    <div class="info-label">{{ $t('p2pClientSettings.peerNatType') }}</div>
                    <div class="info-value">{{ peerNatType }}</div>
                  </div>
                  <div class="info-row" :data-label="$t('p2pClientSettings.peerAddr')">
                    <div class="info-label">{{ $t('p2pClientSettings.peerAddr') }}</div>
                    <div class="info-value">{{ peerAddr }}</div>
                  </div>
                  <div class="info-row" :data-label="$t('p2pClientSettings.peerStatus')">
                    <div class="info-label">{{ $t('p2pClientSettings.peerStatus') }}</div>
                    <div class="info-value" :style="peerStatus.includes('success') ? 'color: #67c23a;' : 'color: #7c7979ff;'">{{ peerStatus }}</div>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- P2P端口映射卡片 -->
          <div class="settings-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('p2pClientSettings.portMapping') }}</h3>
              <div class="btn-group">
                <el-button type="primary" size="small" @click="showAddDialog">{{ $t('p2pClientSettings.addMapping') }}</el-button>
                <el-button type="warning" size="small" @click="stopAll">{{ $t('p2pClientSettings.stopAll') }}</el-button>
                <el-button type="success" size="small" @click="restartAll">{{ $t('p2pClientSettings.restartAll') }}</el-button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="isLoading" class="loading-overlay">
                <el-icon class="is-loading"><Loading /></el-icon>
                {{ $t('p2pClientSettings.loading') }}
              </div>

              <div v-if="!isLoading && mappingList.length === 0" class="empty-state">
                <el-icon><Box /></el-icon>
                <p>{{ $t('p2pClientSettings.noMapping') }}</p>
              </div>

              <div v-if="mappingList.length > 0" class="table-responsive">
                <table class="mapping-table">
                  <thead>
                    <tr>
                      <th>{{ $t('p2pClientSettings.mappingName') }}</th>
                      <th>{{ $t('p2pClientSettings.localPort') }}</th>
                      <th>{{ $t('p2pClientSettings.peerIP') }}</th>
                      <th>{{ $t('p2pClientSettings.peerPort') }}</th>
                      <th>{{ $t('p2pClientSettings.protocol') }}</th>
                      <th>{{ $t('p2pClientSettings.status') }}</th>
                      <th>{{ $t('p2pClientSettings.operation') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in mappingList" :key="item.name">
                      <td :data-label="$t('p2pClientSettings.mappingName')">{{ item.name }}</td>
                      <td :data-label="$t('p2pClientSettings.localPort')">{{ item.listenPort }}</td>
                      <td :data-label="$t('p2pClientSettings.peerIP')">{{ item.connectIP }}</td>
                      <td :data-label="$t('p2pClientSettings.peerPort')">{{ item.connectPort }}</td>
                      <td :data-label="$t('p2pClientSettings.protocol')">{{ item.protocol === 0 ? 'TCP' : 'UDP' }}</td>
                      <td :data-label="$t('p2pClientSettings.status')" class="status-cell">
                        <div :class="getStatusClass(item.result)">
                          {{ item.result }}
                        </div>
                      </td>
                      <td :data-label="$t('p2pClientSettings.operation')" class="action-cell">
                        <div class="action-buttons">
                          <el-button
                            v-if="item.status"
                            type="warning"
                            size="small"
                            @click="stopMapping(item)"
                          >
                            {{ $t('p2pClientSettings.stop') }}
                          </el-button>
                          <template v-else>
                            <el-button
                              type="warning"
                              size="small"
                              @click="startMapping(item)"
                            >
                              {{ $t('p2pClientSettings.start') }}
                            </el-button>
                            <el-button
                              type="primary"
                              size="small"
                              @click="showEditMappingDialog(item)"
                            >
                              {{ $t('p2pClientSettings.edit') }}
                            </el-button>
                            <el-button
                              type="danger"
                              size="small"
                              @click="deleteMapping(item)"
                            >
                              {{ $t('p2pClientSettings.delete') }}
                            </el-button>
                          </template>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增映射对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      :title="isEditingMapping ? $t('p2pClientSettings.editMapping') : $t('p2pClientSettings.addNewMapping')"
      :width="isMobile ? '95%' : '600px'"
      :destroy-on-close="true"
      class="mapping-dialog"
    >
      <el-form
        :model="currentMappingForm"
        :rules="formRules"
        ref="mappingFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('p2pClientSettings.mappingName')" prop="name">
          <el-input
            v-model="currentMappingForm.name"
            :placeholder="$t('p2pClientSettings.namePlaceholder')"
            :disabled="isEditingMapping"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.localPort')" prop="listenPort">
          <el-input
            v-model.number="currentMappingForm.listenPort"
            type="number"
            :placeholder="$t('p2pClientSettings.localPortPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.peerIP')" prop="connectIP">
          <el-input
            v-model="currentMappingForm.connectIP"
            :placeholder="$t('p2pClientSettings.peerIpPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.peerPort')" prop="connectPort">
          <el-input
            v-model.number="currentMappingForm.connectPort"
            type="number"
            :placeholder="$t('p2pClientSettings.localPortPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.protocol')" prop="protocol">
          <el-select v-model="protocolComputed" :placeholder="$t('p2pClientSettings.selectProtocol')">
            <el-option value="tcp" label="TCP" />
            <el-option value="udp" label="UDP" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addDialogVisible = false">{{ $t('p2pClientSettings.cancel') }}</el-button>
          <el-button type="primary" @click="submitMappingForm">{{ $t('p2pClientSettings.save') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 编辑客户端配置对话框 -->
    <el-dialog
      v-model="clientDialogVisible"
      :title="$t('p2pClientSettings.editClientConfig')"
      :width="isMobile ? '95%' : '600px'"
      :destroy-on-close="true"
      class="client-dialog"
    >
      <el-form
        :model="p2pClientForm"
        :rules="clientFormRules"
        ref="clientFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('p2pClientSettings.name')" prop="name">
          <el-input
            v-model="p2pClientForm.name"
            :placeholder="$t('p2pClientSettings.clientNamePlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.secretKey')" prop="token">
          <el-input
            v-model="p2pClientForm.token"
            :placeholder="$t('p2pClientSettings.secretKeyPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.secondKey')">
          <el-input
            v-model="p2pClientForm.secondToken"
            :placeholder="$t('p2pClientSettings.optional')"
          />
        </el-form-item>

        <el-form-item :label="$t('p2pClientSettings.enableRelay')">
          <el-select v-model="p2pClientForm.brFlag" :placeholder="$t('p2pClientSettings.selectEnable')">
            <el-option value="on" :label="$t('p2pClientSettings.enabled')" />
            <el-option value="off" :label="$t('p2pClientSettings.disabled')" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="clientDialogVisible = false">{{ $t('p2pClientSettings.cancel') }}</el-button>
          <el-button type="primary" @click="updateService">{{ $t('p2pClientSettings.saveAndStart') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- P2P日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('p2pClientSettings.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('p2pClientSettings.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('p2pClientSettings.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('p2pClientSettings.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('p2pClientSettings.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('p2pClientSettings.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('p2pClientSettings.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('p2pClientSettings.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('p2pClientSettings.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Loading, Box, Document, InfoFilled } from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config";
import { useRouter } from 'vue-router'

export default {
  name: 'P2PMapping',
  components: { Loading, Box, Document, InfoFilled },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    
    // 页面状态
    const isLoading = ref(false)
    const currentId = ref(0)
    const p2pClientCount = ref(0)
    const loginUsername = ref('admin')
    const refreshInterval = 5000
    const intervalId = ref(null)
    const isMobile = ref(false)

    // 日志相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true)
    const logRefreshInterval = ref(null)

    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    // 数据
    const p2pClient = ref({
      index: 1,
      name: '',
      serverAddr: '',
      token: '',
      defaultToken: '',
      status: false,
      brFlag: false,
      result: '',
      P2PMappingPort: [],
      exportPort: '',
      importPort: ''
    })
    
    const p2pServerAddr = ref('')
    const exportPort = ref('')
    const localIP = ref('')
    const localNatType = ref('')
    const localConnectAddr = ref('')
    const peerFlag = ref(false)
    const peerNatType = ref('')
    const peerAddr = ref('')
    const peerStatus = ref('')
    const secondToken = ref('')
    const token = ref('')
    
    // 映射列表
    const mappingList = ref([])
    
    // 表单相关
    const p2pClientForm = ref({
      name: '',
      token: '',
      secondToken: '',
      brFlag: 'off'
    })
    
    const clientFormRef = ref(null)
    const clientDialogVisible = ref(false)
    
    const clientFormRules = {
      name: [
        { required: true, message: t('p2pClientSettings.nameRequired'), trigger: 'blur' }
      ],
      token: [
        { required: true, message: t('p2pClientSettings.secretKeyRequired'), trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9]{3,20}$/, message: t('p2pClientSettings.secretKeyFormatError'), trigger: 'blur' }
      ]
    }

    const currentMappingForm = ref({
      name: '',
      listenPort: '',
      connectIP: '127.0.0.1',
      connectPort: '',
      protocol: 'tcp'
    })

    const mappingFormRef = ref(null)
    const addDialogVisible = ref(false)
    const isEditingMapping = ref(false)

    // 表单验证规则
    const formRules = {
      name: [
        { required: true, message: t('p2pClientSettings.nameRequired'), trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9]{3,15}$/, message: t('p2pClientSettings.nameFormatError'), trigger: 'blur' }
      ],
      listenPort: [
        { required: true, message: t('p2pClientSettings.localPortRequired'), trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: t('p2pClientSettings.portRangeError'), trigger: 'blur' }
      ],
      connectIP: [
        { required: true, message: t('p2pClientSettings.peerIpRequired'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            const exp = /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/;
            if (exp.test(value)) {
              callback()
            } else {
              callback(new Error(t('p2pClientSettings.ipFormatError')))
            }
          },
          trigger: 'blur'
        }
      ],
      connectPort: [
        { required: true, message: t('p2pClientSettings.peerPortRequired'), trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: t('p2pClientSettings.portRangeError'), trigger: 'blur' }
      ],
      protocol: [
        { required: true, message: t('p2pClientSettings.pleaseSelectProtocol'), trigger: 'change' }
      ]
    }

    const protocolComputed = computed({
      get() {
        return currentMappingForm.value.protocol === 1 || currentMappingForm.value.protocol === 'udp' ? 'udp' : 'tcp';
      },
      set(value) {
        currentMappingForm.value.protocol = value;
      }
    });
    
    // 获取状态样式类
    const getStatusClass = (status) => {
      if (!status) return 'status stopped'

      const lowerStatus = status.toLowerCase()

      if (lowerStatus.includes('failed') || lowerStatus.includes('失败')) {
        return 'status failed'
      } else if (lowerStatus.includes('success') || lowerStatus.includes('running') || lowerStatus.includes('运行中') || lowerStatus.includes('建立成功')) {
        return 'status running'
      } else {
        return 'status stopped'
      }
    }

    // 切换客户端
    const switchClient = (id) => {
      if (id !== currentId.value) {
        currentId.value = id
        fetchP2PList(id)
      }
    }

    // 获取P2P列表数据
    const fetchP2PList = async (id) => {
      try {
        isLoading.value = true
        const response = await fetch(`${API_BASE_URL}/get-p2pList?id=${id}`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(t('p2pClientSettings.fetchFailed'))
        }

        const result = await response.json()

        if (result.errCode === 1000) {
          ElMessage.error(t('common.notLoggedIn'))
          router.push('/user-login?callback=p2p-client')
          return
        }

        if (result.errCode != 0) {
          ElMessage.error(result.errMsg)
          return
        }

        // 更新数据
        p2pClient.value = result.p2pClient || {}
        p2pServerAddr.value = result.p2pServerAddr || ''
        exportPort.value = result.exportPort || ''
        localIP.value = result.localIP || ''
        localNatType.value = result.localNatType || ''
        localConnectAddr.value = result.localConnectAddr || ''
        peerFlag.value = result.peerFlag || false
        peerNatType.value = result.peerNatType || ''
        peerAddr.value = result.peerAddr || ''
        peerStatus.value = result.peerStatus || ''
        secondToken.value = result.secondToken || ''
        token.value = result.token || ''
        p2pClientCount.value = result.P2PClientCount || 0
        loginUsername.value = result.loginUsername || 'admin'

        // 更新表单
        p2pClientForm.value = {
          name: p2pClient.value.name || '',
          token: token.value || '',
          secondToken: secondToken.value || '',
          brFlag: p2pClient.value.brFlag ? 'on' : 'off'
        }

        // 更新映射列表
        mappingList.value = p2pClient.value.P2PMappingPort || []

      } catch (error) {
        console.error(t('p2pClientSettings.fetchFailed'), error)
        ElMessage.error(error.message || t('p2pClientSettings.fetchFailed'))
      } finally {
        isLoading.value = false
      }
    }

    // 开始自动刷新
    const startAutoRefresh = () => {
      stopAutoRefresh()
      intervalId.value = setInterval(() => {
        fetchP2PList(currentId.value)
      }, refreshInterval)
    }

    // 停止自动刷新
    const stopAutoRefresh = () => {
      if (intervalId.value) {
        clearInterval(intervalId.value)
        intervalId.value = null
      }
    }

    // 显示编辑客户端配置对话框
    const showEditClientDialog = () => {
      clientDialogVisible.value = true
    }

    // 更新服务
    const updateService = async () => {
      try {
        await clientFormRef.value.validate()

        if (!p2pClientForm.value.token) {
          ElMessage.error(t('p2pClientSettings.secretKeyRequired'))
          return
        }

        const tokenPattern = /^[a-zA-Z0-9]{3,20}$/
        if (!tokenPattern.test(p2pClientForm.value.token)) {
          ElMessage.error(t('p2pClientSettings.secretKeyFormatError'))
          return
        }

        let finalToken = p2pClientForm.value.token
        if (p2pClientForm.value.secondToken) {
          finalToken = `${p2pClientForm.value.token}@#1@$1@${p2pClientForm.value.secondToken}`
        }

        const params = new URLSearchParams()
        params.append('brFlag', p2pClientForm.value.brFlag)
        params.append('token', finalToken)
        params.append('id', currentId.value)
        params.append('name', p2pClientForm.value.name)

        const response = await fetch(`${API_BASE_URL}/update-p2pClient`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })
        
        if (!response.ok) {
          throw new Error(t('p2pClientSettings.updateFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('p2pClientSettings.updateSuccess'))
        clientDialogVisible.value = false
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || t('p2pClientSettings.updateFailed'))
      }
    }

    // 停止服务
    const stopService = async () => {
      try {
        const params = new URLSearchParams()
        params.append('id', currentId.value)

        const response = await fetch(`${API_BASE_URL}/stop-p2pClient`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(t('p2pClientSettings.stopFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('p2pClientSettings.stopSuccess'))
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || t('p2pClientSettings.stopFailed'))
      }
    }

    // 显示新增映射对话框
    const showAddDialog = () => {
      isEditingMapping.value = false
      currentMappingForm.value = {
        name: '',
        listenPort: '',
        connectIP: '127.0.0.1',
        connectPort: '',
        protocol: 'tcp'
      }
      addDialogVisible.value = true
    }

    // 显示编辑映射对话框
    const showEditMappingDialog = (item) => {
      isEditingMapping.value = true
      currentMappingForm.value = {
        name: item.name,
        listenPort: item.listenPort,
        connectIP: item.connectIP,
        connectPort: item.connectPort,
        protocol: item.protocol === 0 ? 'tcp' : 'udp'
      }
      addDialogVisible.value = true
    }

    // 提交映射表单
    const submitMappingForm = async () => {
      try {
        await mappingFormRef.value.validate()

        const params = new URLSearchParams()
        params.append('name', currentMappingForm.value.name)
        params.append('lport', currentMappingForm.value.listenPort)
        params.append('cport', currentMappingForm.value.connectPort)
        params.append('cip', currentMappingForm.value.connectIP)
        params.append('id', currentId.value)
        params.append('protocol', currentMappingForm.value.protocol === 'tcp' ? 0 : 1)

        const url = isEditingMapping.value ? '/update-p2pListen' : '/start-p2pListen'

        const response = await fetch(`${API_BASE_URL}${url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(isEditingMapping.value ? t('p2pClientSettings.updateFailed') : t('p2pClientSettings.addFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || (isEditingMapping.value ? t('p2pClientSettings.mappingUpdated') : t('p2pClientSettings.mappingAdded')))
        addDialogVisible.value = false
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || (isEditingMapping.value ? t('p2pClientSettings.updateFailed') : t('p2pClientSettings.addFailed')))
      }
    }

    // 开启映射
    const startMapping = async (item) => {
      try {
        const params = new URLSearchParams()
        params.append('name', item.name)
        params.append('lport', item.listenPort)
        params.append('cport', item.connectPort)
        params.append('cip', item.connectIP)
        params.append('id', currentId.value)
        params.append('protocol', item.protocol)

        const response = await fetch(`${API_BASE_URL}/update-p2pListen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(t('p2pClientSettings.startFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('p2pClientSettings.mappingStarted'))
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || t('p2pClientSettings.startFailed'))
      }
    }

    // 停止映射
    const stopMapping = async (item) => {
      try {
        const params = new URLSearchParams()
        params.append('name', item.name)
        params.append('flag', 'off')
        params.append('id', currentId.value)
        
        const response = await fetch(`${API_BASE_URL}/stop-p2pListen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })
        
        if (!response.ok) {
          throw new Error(t('p2pClientSettings.stopFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('p2pClientSettings.stopSuccess'))
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || t('p2pClientSettings.stopFailed'))
      }
    }

    // 删除映射
    const deleteMapping = (item) => {
      ElMessageBox.confirm(t('p2pClientSettings.confirmDeleteMapping', { name: item.name }), t('p2pClientSettings.prompt'), {
        confirmButtonText: t('p2pClientSettings.confirm'),
        cancelButtonText: t('p2pClientSettings.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const params = new URLSearchParams()
          params.append('name', item.name)
          params.append('id', currentId.value)

          const response = await fetch(`${API_BASE_URL}/delete-p2pListen`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            credentials: 'include',
            body: params
          })

          if (!response.ok) {
            throw new Error(t('p2pClientSettings.deleteFailed'))
          }

          const data = await response.json()
          ElMessage.success(data.errMsg || t('p2pClientSettings.deleteSuccess'))
          fetchP2PList(currentId.value)
        } catch (error) {
          ElMessage.error(error.message || t('p2pClientSettings.deleteFailed'))
        }
      }).catch(() => {})
    }

    // 停止所有映射
    const stopAll = async () => {
      try {
        const params = new URLSearchParams()
        params.append('flag', 'on')

        const response = await fetch(`${API_BASE_URL}/stop-all-p2pListen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(t('p2pClientSettings.stopAllFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || t('p2pClientSettings.stoppedAllSuccess'))
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || t('p2pClientSettings.stopAllFailed'))
      }
    }

    // 重启所有映射
    const restartAll = async () => {
      try {
        const params = new URLSearchParams()
        params.append('flag', 'on')

        const response = await fetch(`${API_BASE_URL}/restart-all-p2pListen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(t('p2pClientSettings.restartAllFailed'))
        }

        const data = await response.json()
        if (data.errMsg.includes("not select server")) {
          ElMessage.warning(t('p2pClientSettings.pleaseSelectServer'))
        } else {
          ElMessage.success(data.errMsg || t('p2pClientSettings.restartedAllSuccess'))
        }
        fetchP2PList(currentId.value)
      } catch (error) {
        ElMessage.error(error.message || t('p2pClientSettings.restartAllFailed'))
      }
    }

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success(t('p2pClientSettings.autoRefreshEnabled'))
      } else {
        stopLogAutoRefresh()
        ElMessage.info(t('p2pClientSettings.autoRefreshDisabled'))
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }

        const params = new URLSearchParams({
          module: 'p2p',
          page: logCurrentPage.value,
          size: logPageSize.value
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(t('common.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(t('common.notLoggedIn'))
          router.push('/user-login?callback=p2p-client')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('p2pClientSettings.fetchLogsFailed'))
        }

        logsData.value = data.data || []
        logTotal.value = data.count || 0

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error(t('p2pClientSettings.fetchLogsFailed'), error)
        if (showLoading) {
          ElMessage.error(t('p2pClientSettings.fetchLogsError') + error.message)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(t('p2pClientSettings.confirmClearLogs'), t('p2pClientSettings.prompt'), {
          confirmButtonText: t('p2pClientSettings.confirm'),
          cancelButtonText: t('p2pClientSettings.cancel'),
          type: 'warning'
        })

        logClearing.value = true

        const params = new URLSearchParams({
          module: 'p2p'
        })

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) throw new Error(t('common.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(t('common.notLoggedIn'))
          router.push('/user-login?callback=p2p-client')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('p2pClientSettings.clearLogsFailed'))
        }

        ElMessage.success(t('p2pClientSettings.logsCleared'))
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('清空日志失败:', error)
          ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true
        
        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'p2p',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.error('请先登录系统')
          router.push('/user-login?callback=p2p-client')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        const allLogs = data.data || []
        
        if (allLogs.length === 0) {
          ElMessage.warning('暂无日志可下载')
          return
        }
        
        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `p2p_client_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`)
      } catch (error) {
        console.error('下载日志失败:', error)
        ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }
    
    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }
    
    // 初始化
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchP2PList(currentId.value)
      startAutoRefresh()
    })
    
    // 清理
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      stopAutoRefresh()
      stopLogAutoRefresh()
    })
    
    // 背景样式
    const backgroundStyle = computed(() => ({
      backgroundColor: '#f5f5f5',
      backgroundSize: 'cover'
    }))
    
    return {
      // 状态
      isMobile,
      isLoading,
      currentId,
      p2pClientCount,
      
      // 数据
      p2pClient,
      p2pServerAddr,
      exportPort,
      localIP,
      localNatType,
      localConnectAddr,
      peerFlag,
      peerNatType,
      peerAddr,
      peerStatus,
      mappingList,
      
      // 表单
      formRules,
      clientFormRules,
      p2pClientForm,
      currentMappingForm,
      clientFormRef,
      mappingFormRef,
      clientDialogVisible,
      addDialogVisible,
      isEditingMapping,
      
      // 样式
      backgroundStyle,
      
      // 日志
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      
      protocolComputed,

      // 方法
      switchClient,
      getStatusClass,
      showEditClientDialog,
      updateService,
      stopService,
      showAddDialog,
      showEditMappingDialog,
      submitMappingForm,
      startMapping,
      stopMapping,
      deleteMapping,
      stopAll,
      restartAll,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped lang="scss">
.p2p-mapping-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.top-nav {
  height: 60px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  box-sizing: border-box;
  
  .nav-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 0 20px;
    
    .client-tabs {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;
      
      &.mobile-tabs {
        gap: 4px;
      }
      
      .client-tab {
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.3s;
        white-space: nowrap;
        
        &:hover {
          background-color: #f5f7fa;
        }
        
        &.active {
          background-color: #409eff;
          color: white;
        }
        
        @media (max-width: 768px) {
          padding: 6px 10px;
          font-size: 12px;
        }
        
        @media (max-width: 480px) {
          padding: 4px 8px;
          font-size: 11px;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 50px;
    
    .nav-container {
      padding: 8px 12px;
    }
  }
}

.container {
  width: 100%;
  max-width: 100%;
  padding: 20px 0;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 12px 0;
  }
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  .card-header {
    padding: 20px;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
    
    .btn-group {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      
      .el-button {
        flex-shrink: 0;
      }
    }
  }
  
  .card-body {
    padding: 20px;
    width: 100%;
    box-sizing: border-box;
    
    .form-row {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      
      .form-item {
        flex: 1 1 calc(33.333% - 14px);
        min-width: 200px;
        
        .item-label {
          font-size: 14px;
          color: #606266;
          margin-bottom: 8px;
          font-weight: 500;
        }
        
        .item-value {
          width: 100%;
          
          .el-input {
            width: 100%;
          }
          
          span {
            display: block;
            padding: 8px 0;
            color: #303133;
          }
        }
      }
    }
    
    .node-info {
      .info-row {
        display: flex;
        margin-bottom: 12px;
        padding: 8px 0;
        border-bottom: 1px dashed #ebeef5;
        
        &:last-child {
          border-bottom: none;
        }
        
        .info-label {
          font-weight: 600;
          width: 150px;
          color: #606266;
          flex-shrink: 0;
        }
        
        .info-value {
          flex: 1;
          color: #303133;
          word-break: break-all;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      
      .card-title {
        font-size: 16px;
      }
    }
    
    .card-body {
      padding: 12px;
      
      .form-row {
        gap: 16px;
        
        .form-item {
          flex: 1 1 100%;
          min-width: 100%;
          
          .item-label {
            font-size: 13px;
          }
        }
      }
      
      .node-info {
        .info-row {
          flex-direction: column;
          margin-bottom: 8px;
          padding: 6px 0;
          
          .info-label {
            width: 100%;
            margin-bottom: 4px;
            font-size: 13px;
          }
          
          .info-value {
            font-size: 13px;
            padding-left: 8px;
          }
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
      
      .card-title {
        font-size: 15px;
      }
      
      .btn-group {
        width: 100%;
        
        .el-button--small {
          flex: 1;
          padding: 6px 4px;
          font-size: 12px;
        }
      }
    }
    
    .card-body {
      padding: 8px;
    }
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
    animation: rotating 2s linear infinite;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
}

.status-cell {
  .status {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    
    &.running {
      background-color: #f0f9eb;
      color: #67c23a;
    }
    
    &.stopped {
      background-color: #f4f4f5;
      color: #909399;
    }
    
    &.failed {
      background-color: #fef0f0;
      color: #f56c6c;
    }
  }
}

.action-cell {
  .action-buttons {
    display: flex;
    gap: 4px;
    flex-wrap: nowrap;
    
    .el-button {
      flex-shrink: 0;
    }
    
    .el-button + .el-button {
      margin-left: 0;
    }
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.running {
  color: #67c23a;
  font-weight: 500;
}

/* 移动端表格响应式 */
@media (max-width: 768px) {
  .mapping-table {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
        font-size: 13px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &.status-cell {
        .status {
          margin-left: auto;
        }
      }
      
      &.action-cell {
        padding: 10px 12px;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
        
        .action-buttons {
          flex: 1;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 4px;
          
          .el-button--small {
            padding: 4px 8px;
            font-size: 12px;
          }
        }
      }
    }
  }
  
  .mapping-dialog,
  .client-dialog {
    :deep(.el-dialog) {
      margin: 8px !important;
    }
    
    :deep(.el-dialog__body) {
      padding: 16px;
    }
    
    :deep(.el-dialog__header) {
      padding: 16px;
    }
    
    :deep(.el-dialog__footer) {
      padding: 16px;
    }
  }
}

@media (max-width: 480px) {
  .mapping-table {
    td {
      padding: 8px 10px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
      
      &.action-cell {
        .action-buttons {
          .el-button--small {
            padding: 3px 6px;
            font-size: 11px;
          }
        }
      }
    }
  }
  
  .status-cell {
    .status {
      padding: 2px 6px;
      font-size: 11px;
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  width: 100%;
  
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>